import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ROLE_PATHS } from "../../config/config";
import { soulManager } from "../managers/SoulManager";
import { SoulDataModel } from "../models/Soul/SoulDataModel";

export interface UserDataContextType {
  data?: SoulDataModel;
  permittedPaths: string[];
  loading: boolean;
  error?: string;
  reloadUserData: () => Promise<void>;
}

const UserDataContext = createContext<UserDataContextType | undefined>(
  undefined
);

export const UserDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<SoulDataModel>();
  const [permittedPaths, setPermittedPaths] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();

  const getUserData = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await soulManager.getSoul();
      setUserData(data);
      const role = (data?.role?.toUpperCase() ||
        "USER") as keyof typeof ROLE_PATHS;
      setPermittedPaths(ROLE_PATHS[role] ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load user data");
      setUserData(undefined);
      setPermittedPaths([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // carga inicial
    getUserData();
  }, []);

  const value = useMemo<UserDataContextType>(
    () => ({
      data: userData,
      permittedPaths,
      loading,
      error,
      reloadUserData: getUserData,
    }),
    [userData, permittedPaths, loading, error]
  );

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export function useUserData() {
  const ctx = useContext(UserDataContext);
  if (!ctx)
    throw new Error("useUserData must be used within <UserDataProvider>");
  return ctx;
}
